/**
 * BusinessEmailOnboarding.tsx — post-purchase 9-step wizard.
 *
 * Route: /dashboard/email/onboard/:id  (where :id is the mx1_mail_domains row id)
 *
 * The customer arrives here right after paying for a Business Email package.
 * Instead of dumping them on the dashboard with "go figure it out," we walk
 * them through:
 *
 *   0. Welcome — orientation, "5 minutes from here to live"
 *   1. Role — owner vs IT admin (decides downstream UI density)
 *   2. Fresh vs migrate
 *   3. Source provider (if migrating)
 *   4. Cutover timing (if migrating)
 *   5. DNS pointing — wraps the existing EmailSetup wizard inline
 *   6. Mailbox creation — wraps EmailMailboxes inline (single mailbox for
 *      fresh; bulk-CSV will come in a later milestone)
 *   7. Migration kickoff — wraps EmailMigrate inline (single-mailbox today;
 *      bulk is a placeholder showing the planned UX)
 *   8. Cutover — confirm DNS is live, do a delta sync (placeholder),
 *      send a test mail
 *   9. Done — webmail link, IMAP/SMTP settings, "you're live"
 *
 * State is persisted on mx1_mail_domains.onboarding_step + onboarding_answers
 * via /api/email/setup/v2/:id/onboarding. The user can close the browser
 * anywhere mid-wizard; on return the wizard opens at the highest step they
 * reached.
 *
 * Color policy: brand palette only (navy + secondary-yellow). No reds/greens.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/environment';
import { MailConnectionFields, resolveMailSettings } from '../config/mail';
import DashShell from '../components/dash/DashShell';

// ---------------------------------------------------------------------------
// Types

type Role = 'owner' | 'admin';
type FreshOrMigrate = 'fresh' | 'migrate';
type SourceProvider = 'gmail' | 'm365' | 'zoho' | 'other' | 'unknown';
type CutoverTiming = 'now' | 'week' | 'exploring';

interface Answers {
  role?: Role;
  freshOrMigrate?: FreshOrMigrate;
  sourceProvider?: SourceProvider;
  cutoverTiming?: CutoverTiming;
}

interface OnboardingState {
  domain: string;
  provisioning_status: string;
  manual_dns_required: boolean;
  business_email_subscription_id: string;
  step: number;
  answers: Answers;
}

// ---------------------------------------------------------------------------
// Steps definition — labels for the progress strip + step counts for skipping

const STEPS: { idx: number; label: string }[] = [
  { idx: 0, label: 'Welcome' },
  { idx: 1, label: 'Your role' },
  { idx: 2, label: 'Fresh or migrate' },
  { idx: 3, label: 'Source provider' },
  { idx: 4, label: 'Cutover timing' },
  { idx: 5, label: 'DNS pointing' },
  { idx: 6, label: 'Mailboxes' },
  { idx: 7, label: 'Migration' },
  { idx: 8, label: 'Cutover' },
  { idx: 9, label: 'Done' },
];

// Steps that get skipped if the customer chose 'fresh' (no migration needed).
const MIGRATE_ONLY_STEPS = new Set([3, 4, 7, 8]);

function nextStep(current: number, answers: Answers): number {
  let n = current + 1;
  while (n <= 9 && answers.freshOrMigrate === 'fresh' && MIGRATE_ONLY_STEPS.has(n)) n++;
  return Math.min(n, 9);
}
function prevStep(current: number, answers: Answers): number {
  let n = current - 1;
  while (n >= 0 && answers.freshOrMigrate === 'fresh' && MIGRATE_ONLY_STEPS.has(n)) n--;
  return Math.max(n, 0);
}

// ---------------------------------------------------------------------------
// API helpers

const token = () => localStorage.getItem('token');
async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(getApiUrl(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token()}`,
      ...(init.headers || {}),
    },
  });
  const body: any = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err: any = new Error(body?.error || body?.message || `Request failed (HTTP ${r.status})`);
    err.status = r.status;
    throw err;
  }
  return body as T;
}

/** Friendly wording for the status codes the mailbox API uses. */
const friendlyError = (e: any, fallback: string): string => {
  if (e?.status === 409) return e.message || 'That address already exists — pick a different name.';
  if (e?.status === 423) return e.message || 'This subscription is suspended or cancelled, so changes are locked. Check Billing or contact support.';
  return e?.message || fallback;
};

// mx1 provisioning_status values that mean "DNS is in and we're past it".
const DNS_DONE_STATUSES = new Set(['dns_verified', 'ses_pending', 'ses_verified', 'provisioning_mailbox', 'active']);

/** What InlineDnsPanel reports back to the wizard so the gate updates live. */
interface DnsStatus {
  status: string;
  uiStage: string;
  ownershipVerified: boolean;
}

/**
 * Connection settings for the Done/Cutover steps. Prefers the hostnames on
 * the v2 setup payload; MAIL_HOSTS is only the fallback.
 */
function useMailSettings(domainRowId: string) {
  const [src, setSrc] = useState<MailConnectionFields | null>(null);
  useEffect(() => {
    if (!domainRowId) return;
    let cancelled = false;
    api<{ success: boolean; data?: MailConnectionFields }>(`/email/setup/v2/${domainRowId}`)
      .then(j => { if (!cancelled && j.success && j.data) setSrc(j.data); })
      .catch(() => { /* fall back to MAIL_HOSTS */ });
    return () => { cancelled = true; };
  }, [domainRowId]);
  return resolveMailSettings(src);
}

// ---------------------------------------------------------------------------
// Wizard

const BusinessEmailOnboarding: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Local cursor — what step the user is viewing right now. May differ from
  // state.step (which is the high-water mark we've persisted).
  const [cursor, setCursor] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  // Save failures are shown inline so the wizard stays usable (the page-level
  // `error` is reserved for "couldn't load at all").
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dnsStatus, setDnsStatus] = useState<DnsStatus | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const r = await api<{ success: boolean; data: OnboardingState; error?: string }>(`/email/setup/v2/${id}/onboarding`);
      if (!r.success) { setError(r.error || 'Failed to load'); return; }
      setState(r.data);
      // Open at the persisted high-water step on first load.
      setCursor((c) => (c === null ? r.data.step : c));
      setError(null);
    } catch (e: any) { setError(e?.message || 'Network error'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveAnswers = useCallback(async (patch: Partial<Answers>, nextCursor?: number) => {
    if (!id) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body: any = { answers: patch };
      if (typeof nextCursor === 'number') body.step = nextCursor;
      const r = await api<{ success: boolean; data: { step: number; answers: Answers }; error?: string }>(
        `/email/setup/v2/${id}/onboarding`,
        { method: 'POST', body: JSON.stringify(body) },
      );
      if (r.success && r.data) {
        setState((s) => s ? { ...s, step: r.data.step, answers: r.data.answers } : s);
        if (typeof nextCursor === 'number') setCursor(nextCursor);
      } else {
        setSaveError(r.error || 'Couldn\'t save your progress');
      }
    } catch (e: any) {
      setSaveError(e?.message || 'Couldn\'t save your progress');
    } finally { setSaving(false); }
  }, [id]);

  // Live status from the DNS panel so the Continue gate updates without a
  // full reload of the onboarding state.
  const onDnsStatus = useCallback((st: DnsStatus) => {
    setState((s) => s && s.provisioning_status !== st.status ? { ...s, provisioning_status: st.status } : s);
    setDnsStatus(st);
  }, []);

  // Error must render BEFORE the loading skeleton — otherwise a failed load
  // (state stays null) leaves the page spinning forever.
  if (error) {
    return (
      <DashShell title="Set up your Business Email" subtitle="Couldn't load">
        <div role="alert" className="rounded-2xl bg-secondary-100 border border-secondary-500 p-6 max-w-md mx-auto text-center">
          <p className="text-primary-navy mb-3">{error}</p>
          <button type="button" onClick={() => { setLoading(true); setError(null); load(); }} className="text-sm bg-primary-navy text-white px-3 py-1.5 rounded-lg mr-3">Retry</button>
          <Link to="/customer/email" className="text-sm text-primary-navy hover:underline">← Back to email dashboard</Link>
        </div>
      </DashShell>
    );
  }
  if (loading || !state || cursor === null) {
    return (
      <DashShell title="Set up your Business Email" subtitle="Loading…">
        <div className="h-32 rounded-2xl bg-white/60 backdrop-blur animate-pulse" />
      </DashShell>
    );
  }

  const goto = (n: number) => setCursor(Math.max(0, Math.min(9, n)));
  const advance = (patch: Partial<Answers> = {}) => {
    const merged = { ...state.answers, ...patch };
    const n = nextStep(cursor, merged);
    saveAnswers(patch, n);
  };
  const back = () => goto(prevStep(cursor, state.answers));

  return (
    <DashShell
      title="Set up your Business Email"
      subtitle={`${state.domain} · about 5 minutes from here to live`}
      right={
        <Link to="/customer/email" className="text-sm text-primary-navy hover:underline">
          ← Email dashboard
        </Link>
      }
    >
      <ProgressStrip cursor={cursor} answers={state.answers} highWater={state.step} onJump={goto} />

      <div className="mt-6">
        {cursor === 0 && <StepWelcome state={state} onNext={() => advance({})} />}
        {cursor === 1 && <StepRole state={state} onAnswer={(role) => advance({ role })} onBack={back} />}
        {cursor === 2 && <StepFreshOrMigrate state={state} onAnswer={(v) => advance({ freshOrMigrate: v })} onBack={back} />}
        {cursor === 3 && <StepSourceProvider state={state} onAnswer={(v) => advance({ sourceProvider: v })} onBack={back} />}
        {cursor === 4 && <StepCutoverTiming state={state} onAnswer={(v) => advance({ cutoverTiming: v })} onBack={back} />}
        {cursor === 5 && <StepDnsPointing state={state} dnsStatus={dnsStatus} onStatus={onDnsStatus} onNext={() => advance({})} onBack={back} />}
        {cursor === 6 && <StepMailboxes state={state} onNext={() => advance({})} onBack={back} />}
        {cursor === 7 && <StepMigration state={state} onNext={() => advance({})} onBack={back} />}
        {cursor === 8 && <StepCutover state={state} onNext={() => advance({})} onBack={back} />}
        {cursor === 9 && <StepDone state={state} onFinish={() => navigate('/customer/email')} />}
      </div>

      {saving && (
        <div className="mt-4 text-xs text-slate-500 text-right" aria-live="polite">Saving…</div>
      )}
      {saveError && (
        <div role="alert" className="mt-4 text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-3">
          🚨 {saveError} <button type="button" onClick={() => setSaveError(null)} className="underline ml-2">Dismiss</button>
        </div>
      )}
    </DashShell>
  );
};

// ---------------------------------------------------------------------------
// Progress strip — small horizontal step indicators, clickable to jump back to
// already-completed steps.

const ProgressStrip: React.FC<{
  cursor: number;
  answers: Answers;
  highWater: number;
  onJump: (n: number) => void;
}> = ({ cursor, answers, highWater, onJump }) => {
  // Filter out migrate-only steps if the customer chose fresh, so the strip
  // doesn't show grayed-out steps they'll never visit.
  const visible = STEPS.filter(s => !(answers.freshOrMigrate === 'fresh' && MIGRATE_ONLY_STEPS.has(s.idx)));
  return (
    <div className="bg-white/80 backdrop-blur border border-white/40 rounded-2xl p-4 shadow-sm">
      <ol className="flex items-center gap-1 overflow-x-auto">
        {visible.map((s, i) => {
          const isActive = s.idx === cursor;
          const isDone = s.idx < cursor || s.idx <= highWater;
          const clickable = s.idx <= highWater;
          return (
            <li key={s.idx} className="flex items-center min-w-0">
              <button
                onClick={() => clickable && onJump(s.idx)}
                disabled={!clickable}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition whitespace-nowrap ${
                  isActive
                    ? 'bg-secondary-500 text-primary-navy font-semibold'
                    : isDone
                      ? 'bg-secondary-100 text-primary-navy hover:bg-secondary-200'
                      : 'bg-slate-100 text-slate-400'
                } ${clickable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              >
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-primary-navy text-white' :
                  isDone   ? 'bg-primary-navy text-white' :
                             'bg-slate-300 text-white'
                }`}>
                  {isDone && !isActive ? '✓' : i + 1}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < visible.length - 1 && (
                <span className="w-2 h-px bg-slate-300 mx-0.5" aria-hidden />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Reusable card chrome

const Card: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; footer?: React.ReactNode }> = ({ title, subtitle, children, footer }) => (
  <div className="bg-white/85 backdrop-blur border border-white/40 rounded-2xl shadow-sm">
    <div className="p-6 border-b border-slate-100">
      <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
    </div>
    <div className="p-6">{children}</div>
    {footer && (
      <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
        {footer}
      </div>
    )}
  </div>
);

const NavBtns: React.FC<{
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
}> = ({ onBack, onNext, nextLabel = 'Continue', nextDisabled = false }) => (
  <>
    {onBack ? (
      <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">
        ← Back
      </button>
    ) : <span />}
    {onNext && (
      <button
        onClick={onNext}
        disabled={nextDisabled}
        className="text-sm bg-primary-navy text-white font-medium px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition"
      >
        {nextLabel} →
      </button>
    )}
  </>
);

const ChoiceCard: React.FC<{
  active?: boolean;
  onClick: () => void;
  title: string;
  desc: string;
  hint?: string;
}> = ({ active, onClick, title, desc, hint }) => (
  <button
    onClick={onClick}
    className={`text-left w-full border rounded-xl p-4 transition ${
      active
        ? 'border-secondary-500 bg-secondary-50 shadow-sm'
        : 'border-slate-200 bg-white hover:border-secondary-500 hover:bg-secondary-50/50'
    }`}
  >
    <div className="font-medium text-slate-900">{title}</div>
    <div className="text-sm text-slate-600 mt-1">{desc}</div>
    {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
  </button>
);

// ---------------------------------------------------------------------------
// Step 0 — Welcome

const StepWelcome: React.FC<{ state: OnboardingState; onNext: () => void }> = ({ state, onNext }) => (
  <Card
    title={`Welcome — let's set up email for ${state.domain}`}
    subtitle="Five minutes from here to a live mailbox. You can pause and come back any time; we'll resume where you left off."
    footer={<NavBtns onNext={onNext} nextLabel="Let's go" />}
  >
    <div className="space-y-4 text-sm text-slate-700">
      <p>
        Here's what we'll do together:
      </p>
      <ol className="list-decimal list-inside space-y-1 text-slate-700">
        <li>A couple of quick questions about your situation.</li>
        <li>Point your domain at our mail servers (a few DNS records to copy/paste at your registrar).</li>
        <li>Create your first mailboxes.</li>
        <li>If you're switching from Gmail/Microsoft/Zoho, copy your old mail across.</li>
        <li>Cut over so new mail starts arriving in your new mailbox.</li>
      </ol>
      <p className="text-slate-500">
        No part of this is irreversible. If you get stuck, contact support — we'll finish the move with you.
      </p>
    </div>
  </Card>
);

// ---------------------------------------------------------------------------
// Step 1 — Role

const StepRole: React.FC<{ state: OnboardingState; onAnswer: (r: Role) => void; onBack: () => void }> = ({ state, onAnswer, onBack }) => {
  const [val, setVal] = useState<Role | undefined>(state.answers.role);
  return (
    <Card
      title="Quick question — who are you?"
      subtitle="So we can show you the right path. You can change your answer later."
      footer={<NavBtns onBack={onBack} onNext={val ? () => onAnswer(val) : undefined} nextDisabled={!val} />}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ChoiceCard
          active={val === 'owner'}
          onClick={() => setVal('owner')}
          title="I'm the owner / it's just me"
          desc="A small business, freelance, or just starting out."
          hint="We'll keep instructions short and skip the IT jargon."
        />
        <ChoiceCard
          active={val === 'admin'}
          onClick={() => setVal('admin')}
          title="I'm an IT admin for a team"
          desc="Multiple mailboxes, possibly an existing email setup to move from."
          hint="We'll show bulk tools (CSV imports, batch migrations) where applicable."
        />
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Step 2 — Fresh or migrate

const StepFreshOrMigrate: React.FC<{ state: OnboardingState; onAnswer: (v: FreshOrMigrate) => void; onBack: () => void }> = ({ state, onAnswer, onBack }) => {
  const [val, setVal] = useState<FreshOrMigrate | undefined>(state.answers.freshOrMigrate);
  return (
    <Card
      title="Are you starting fresh, or moving from another provider?"
      footer={<NavBtns onBack={onBack} onNext={val ? () => onAnswer(val) : undefined} nextDisabled={!val} />}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ChoiceCard
          active={val === 'fresh'}
          onClick={() => setVal('fresh')}
          title="🆕 Fresh start"
          desc="No existing mailboxes to copy across. Brand-new email on this domain."
          hint="We'll skip migration steps and go straight to mailbox creation."
        />
        <ChoiceCard
          active={val === 'migrate'}
          onClick={() => setVal('migrate')}
          title="📦 Migrate from another provider"
          desc="You have email on Gmail, Microsoft 365, Zoho, or another IMAP host and want to bring it across."
          hint="We'll guide you through the copy + cutover so you don't lose mail."
        />
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Step 3 — Source provider

const StepSourceProvider: React.FC<{ state: OnboardingState; onAnswer: (v: SourceProvider) => void; onBack: () => void }> = ({ state, onAnswer, onBack }) => {
  const [val, setVal] = useState<SourceProvider | undefined>(state.answers.sourceProvider);
  const options: { v: SourceProvider; title: string; desc: string }[] = [
    { v: 'gmail',   title: 'Gmail / Google Workspace', desc: 'Personal Gmail or a Google Workspace tenant.' },
    { v: 'm365',    title: 'Microsoft 365 / Outlook',  desc: 'M365 mailboxes, Exchange Online, or hosted Exchange.' },
    { v: 'zoho',    title: 'Zoho Mail',                desc: 'Zoho Mail or Zoho Workplace.' },
    { v: 'other',   title: 'Another IMAP provider',    desc: 'cPanel, SquirrelMail, hosted Postfix, anything that speaks IMAP.' },
    { v: 'unknown', title: 'Not sure yet',             desc: 'We can come back to this — choose this and we\'ll ask again later.' },
  ];
  return (
    <Card
      title="Where are you migrating from?"
      subtitle="We'll tailor the next step's instructions to your old provider."
      footer={<NavBtns onBack={onBack} onNext={val ? () => onAnswer(val) : undefined} nextDisabled={!val} />}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {options.map(o => (
          <ChoiceCard
            key={o.v}
            active={val === o.v}
            onClick={() => setVal(o.v)}
            title={o.title}
            desc={o.desc}
          />
        ))}
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Step 4 — Cutover timing

const StepCutoverTiming: React.FC<{ state: OnboardingState; onAnswer: (v: CutoverTiming) => void; onBack: () => void }> = ({ state, onAnswer, onBack }) => {
  const [val, setVal] = useState<CutoverTiming | undefined>(state.answers.cutoverTiming);
  return (
    <Card
      title="When are you planning to actually switch?"
      subtitle="This decides whether we set up a side-by-side test phase, or move you straight across."
      footer={<NavBtns onBack={onBack} onNext={val ? () => onAnswer(val) : undefined} nextDisabled={!val} />}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ChoiceCard
          active={val === 'now'}
          onClick={() => setVal('now')}
          title="Move me now"
          desc="Cut over as soon as the records propagate and migration's done."
          hint="Best for ≤5 mailboxes, low daily volume."
        />
        <ChoiceCard
          active={val === 'week'}
          onClick={() => setVal('week')}
          title="Within a week"
          desc="I want to test the new setup for a few days before flipping the MX."
          hint="We'll keep your old mail flowing while you validate ours."
        />
        <ChoiceCard
          active={val === 'exploring'}
          onClick={() => setVal('exploring')}
          title="Just exploring"
          desc="Not switching yet — I want to set things up first."
          hint="We'll prepare everything except the cutover. You flip when you're ready."
        />
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Step 5 — DNS pointing
// Rendered inline via InlineDnsPanel (see bottom of file). Shares auth/state
// with the wizard; the standalone /dashboard/email/setup/v2/:id route still
// exists for direct access from the dashboard.

const StepDnsPointing: React.FC<{
  state: OnboardingState;
  dnsStatus: DnsStatus | null;
  onStatus: (st: DnsStatus) => void;
  onNext: () => void;
  onBack: () => void;
}> = ({ state, dnsStatus, onStatus, onNext, onBack }) => {
  const domainRowId = useDomainRowId();
  const liveStatus = dnsStatus?.status || state.provisioning_status;
  const fullyDone = !state.manual_dns_required || DNS_DONE_STATUSES.has(liveStatus);
  // Customers who aren't cutting over yet ('week' / 'exploring') only need
  // ownership proven — they can keep MX at the old provider for now.
  const softTiming = state.answers.cutoverTiming === 'week' || state.answers.cutoverTiming === 'exploring';
  const ownershipOk = !!dnsStatus?.ownershipVerified || DNS_DONE_STATUSES.has(liveStatus);
  const blocked = liveStatus === 'failed' || liveStatus === 'suspended' || liveStatus === 'terminated';
  const canContinue = !blocked && (fullyDone || (softTiming && ownershipOk));
  const nextLabel = fullyDone
    ? 'Continue →'
    : (softTiming && ownershipOk) ? 'Continue (MX can move later) →' : 'Waiting for DNS…';
  return (
    <Card
      title="Point your domain at our mail servers"
      subtitle="Add these records at your DNS provider (GoDaddy, Cloudflare, Route 53, whoever holds your domain). The wizard checks every 60 seconds and marks each row as it sees the record."
      footer={
        <>
          <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">← Back</button>
          <button
            onClick={onNext}
            disabled={!canContinue}
            className="text-sm bg-primary-navy text-white font-medium px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition"
            title={canContinue ? '' : (blocked ? 'This domain needs attention before you can continue.' : 'Add the DNS records above and wait for them to be detected before continuing.')}
          >
            {nextLabel}
          </button>
        </>
      }
    >
      <InlineDnsPanel domainRowId={domainRowId} onStatus={onStatus} />
      {softTiming && !fullyDone && ownershipOk && (
        <p className="text-xs text-slate-600 mt-3">
          Ownership is verified, so you can carry on setting up mailboxes. Add the MX record whenever you're ready to receive mail here.
        </p>
      )}
      <p className="text-xs text-slate-500 mt-3">
        Tip: if a record isn't detected within a few minutes, double-check the host field at your registrar — where we show
        <span className="font-mono"> @ </span>as the host, that means the domain itself. Enter it exactly as shown; if your registrar
        uses an empty host field for the apex instead, leave it empty.
      </p>
    </Card>
  );
};

// Tiny hook so each step can read the URL param without us threading it down.
function useDomainRowId(): string {
  const { id } = useParams<{ id: string }>();
  return id || '';
}

// ---------------------------------------------------------------------------
// Step 6 — Mailbox creation

const StepMailboxes: React.FC<{ state: OnboardingState; onNext: () => void; onBack: () => void }> = ({ state, onNext, onBack }) => {
  const id = useDomainRowId();
  const [bulkOpen, setBulkOpen] = useState(false);
  return (
    <Card
      title="Create your first mailbox"
      subtitle={
        state.answers.freshOrMigrate === 'migrate'
          ? 'Create destination mailboxes here that match your existing mailbox addresses. For more than a few, use the bulk CSV importer below.'
          : 'Add the addresses you want — e.g. you@' + state.domain + '. You can add more any time from Manage Mailboxes.'
      }
      footer={
        <>
          <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">← Back</button>
          <button onClick={onNext} className="text-sm bg-primary-navy text-white font-medium px-4 py-2 rounded-lg hover:bg-primary-700 transition">
            Continue →
          </button>
        </>
      }
    >
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setBulkOpen(v => !v)}
          className="text-sm font-medium text-primary-navy hover:underline"
        >
          {bulkOpen ? '▾ Hide bulk CSV importer' : '▸ Bulk-create mailboxes from CSV'}
        </button>
        {bulkOpen && id && <BulkMailboxCsv domainRowId={id} domain={state.domain} />}
      </div>
      {id && <InlineMailboxesPanel domainRowId={id} domain={state.domain} />}
    </Card>
  );
};

interface BulkMailboxRow { localPart: string; password: string; quotaMb?: number }
interface BulkResultRow { localPart: string; ok: boolean; error?: string; id?: number }

/**
 * Parse a CSV-ish blob (or pasted lines) into mailbox rows. Accepts:
 *   localpart,password
 *   localpart,password,quota_mb
 * Lines starting with # or // are comments. The localpart can also be
 * a full email "you@domain" — we strip the @-part to be forgiving.
 */
function parseMailboxCsv(text: string): { rows: BulkMailboxRow[]; errors: string[] } {
  const out: BulkMailboxRow[] = [];
  const errors: string[] = [];
  const lines = text.split(/\r?\n/);
  let lineNo = 0;
  for (const raw of lines) {
    lineNo += 1;
    const line = raw.trim();
    if (!line || line.startsWith('#') || line.startsWith('//')) continue;
    const parts = line.split(',').map(s => s.trim());
    if (parts.length < 2) { errors.push(`line ${lineNo}: need at least localpart,password`); continue; }
    let localPart = parts[0].toLowerCase();
    if (localPart.includes('@')) localPart = localPart.split('@')[0];
    const password = parts[1];
    const quotaMb = parts[2] ? Number(parts[2]) : undefined;
    if (!/^[a-z0-9._-]+$/.test(localPart)) { errors.push(`line ${lineNo}: invalid localpart "${localPart}"`); continue; }
    if (password.length < 8) { errors.push(`line ${lineNo}: password must be ≥8 chars`); continue; }
    out.push({ localPart, password, ...(quotaMb && !isNaN(quotaMb) ? { quotaMb } : {}) });
  }
  return { rows: out, errors };
}

const BulkMailboxCsv: React.FC<{ domainRowId: string; domain: string }> = ({ domainRowId, domain }) => {
  const [text, setText] = useState('');
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parsed, setParsed] = useState<BulkMailboxRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState<BulkResultRow[] | null>(null);

  const onParse = () => {
    const r = parseMailboxCsv(text);
    setParseErrors(r.errors);
    setParsed(r.rows);
    setReport(null);
  };

  const onFile = (f: File) => {
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result || '')); };
    reader.readAsText(f);
  };

  const onSubmit = async () => {
    if (parsed.length === 0) return;
    setSubmitting(true);
    setReport(null);
    try {
      const r = await fetch(getApiUrl(`/email/setup/v2/domains/${domainRowId}/mailboxes/bulk`), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ mailboxes: parsed }),
      });
      const j = await r.json();
      if (!r.ok || !j?.success) {
        setReport([{ localPart: '(request)', ok: false, error: j?.error || `HTTP ${r.status}` }]);
      } else {
        setReport(j.data.results as BulkResultRow[]);
      }
    } catch (e: any) {
      setReport([{ localPart: '(network)', ok: false, error: e?.message || 'request failed' }]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <div className="text-xs text-slate-600">
        Paste rows like <code>localpart,password</code> (one per line). Optional 3rd field is quota in MB.
        You can also drop a CSV file. Lines beginning with <code>#</code> are ignored.
        Example: <code>alice,Strong-Pass-1</code> creates <code>alice@{domain}</code>.
      </div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={`alice,${'P'}assw0rd-Alice\nbob,${'P'}assw0rd-Bob,2048\n# you can mix comments`}
        rows={6}
        className="w-full font-mono text-xs rounded border border-slate-300 p-2"
      />
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs text-slate-700 cursor-pointer">
          <input type="file" accept=".csv,text/csv,text/plain" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
          <span className="px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Choose CSV…</span>
        </label>
        <button type="button" onClick={onParse}
          className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">
          Parse
        </button>
        <button type="button" onClick={onSubmit} disabled={submitting || parsed.length === 0}
          className="text-xs px-3 py-1.5 rounded bg-primary-navy text-white font-medium hover:bg-primary-700 disabled:opacity-50">
          {submitting ? 'Creating…' : `Create ${parsed.length || ''} mailbox${parsed.length === 1 ? '' : 'es'}`}
        </button>
      </div>
      {parseErrors.length > 0 && (
        <ul role="alert" className="text-xs text-primary-navy list-disc pl-5">
          {parseErrors.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
      {parsed.length > 0 && !report && (
        <div className="text-xs text-slate-600">{parsed.length} valid row{parsed.length === 1 ? '' : 's'} ready.</div>
      )}
      {report && (
        <div className="text-xs space-y-1">
          <div className="font-medium">
            {report.filter(r => r.ok).length} succeeded, {report.filter(r => !r.ok).length} failed
          </div>
          <ul className="max-h-48 overflow-y-auto border border-slate-200 rounded p-2 bg-slate-50">
            {report.map((r, i) => (
              <li key={i} className={r.ok ? 'text-primary-navy' : 'text-primary-navy font-medium'}>
                {r.ok ? '✓' : '✗'} {r.localPart}{r.error ? ` — ${r.error}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 7 — Migration

const StepMigration: React.FC<{ state: OnboardingState; onNext: () => void; onBack: () => void }> = ({ state, onNext, onBack }) => {
  const id = useDomainRowId();
  const isBulkUseful = state.answers.role === 'admin';
  const [bulkOpen, setBulkOpen] = useState(false);
  return (
    <Card
      title={
        state.answers.sourceProvider === 'gmail' ? 'Migrate your mail from Gmail / Google Workspace' :
        state.answers.sourceProvider === 'm365'  ? 'Migrate your mail from Microsoft 365' :
        state.answers.sourceProvider === 'zoho'  ? 'Migrate your mail from Zoho' :
        'Migrate your mail across'
      }
      subtitle={
        isBulkUseful
          ? 'Submit one migration job per mailbox, or use the bulk CSV importer to queue many at once.'
          : 'Submit a job for each mailbox you want to copy. The copy runs server-side; you don\'t have to keep this tab open.'
      }
      footer={
        <>
          <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">← Back</button>
          <button onClick={onNext} className="text-sm bg-primary-navy text-white font-medium px-4 py-2 rounded-lg hover:bg-primary-700 transition">
            Migration started, continue →
          </button>
        </>
      }
    >
      <ProviderHints provider={state.answers.sourceProvider} />
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setBulkOpen(v => !v)}
          className="text-sm font-medium text-primary-navy hover:underline"
        >
          {bulkOpen ? '▾ Hide bulk CSV migration importer' : '▸ Bulk-queue migrations from CSV'}
        </button>
        {bulkOpen && id && (
          <BulkMigrationCsv
            domainRowId={id}
            domain={state.domain}
            defaultSourceHost={
              state.answers.sourceProvider === 'gmail' ? 'imap.gmail.com' :
              state.answers.sourceProvider === 'm365'  ? 'outlook.office365.com' :
              state.answers.sourceProvider === 'zoho'  ? 'imap.zoho.com' : ''
            }
          />
        )}
      </div>
      {id && <InlineMigrationPanel domainRowId={id} domain={state.domain} sourceProvider={state.answers.sourceProvider} />}
    </Card>
  );
};

interface BulkMigRow { sourceUser: string; sourcePassword: string; destEmail: string; destPassword: string }
interface BulkMigResult { row: number; destEmail: string; ok: boolean; error?: string; jobId?: string }

/**
 * CSV columns (one row per mailbox to migrate):
 *   source_user, source_password, dest_email, dest_password
 * dest_email defaults to source_user@<this domain> if blank — that's
 * the common case where the customer is keeping their old addresses.
 */
function parseMigrationCsv(text: string, defaultDestDomain: string): { rows: BulkMigRow[]; errors: string[] } {
  const out: BulkMigRow[] = [];
  const errors: string[] = [];
  const lines = text.split(/\r?\n/);
  let n = 0;
  for (const raw of lines) {
    n += 1;
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const parts = line.split(',').map(s => s.trim());
    if (parts.length < 2) { errors.push(`line ${n}: need at least source_user,source_password`); continue; }
    const sourceUser = parts[0];
    const sourcePassword = parts[1];
    let destEmail = parts[2] || '';
    const destPassword = parts[3] || sourcePassword;
    if (!destEmail) {
      const local = sourceUser.includes('@') ? sourceUser.split('@')[0] : sourceUser;
      destEmail = `${local}@${defaultDestDomain}`;
    }
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(destEmail)) {
      errors.push(`line ${n}: invalid dest_email "${destEmail}"`); continue;
    }
    out.push({ sourceUser, sourcePassword, destEmail: destEmail.toLowerCase(), destPassword });
  }
  return { rows: out, errors };
}

const BulkMigrationCsv: React.FC<{ domainRowId: string; domain: string; defaultSourceHost: string }> = ({ domainRowId, domain, defaultSourceHost }) => {
  const [sourceHost, setSourceHost] = useState(defaultSourceHost);
  const [sourcePort, setSourcePort] = useState('993');
  const [useSsl, setUseSsl] = useState(true);
  const [consent, setConsent] = useState(false);
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<BulkMigRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState<BulkMigResult[] | null>(null);

  const onParse = () => {
    const r = parseMigrationCsv(text, domain);
    setParseErrors(r.errors);
    setParsed(r.rows);
    setReport(null);
  };

  const onFile = (f: File) => {
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result || '')); };
    reader.readAsText(f);
  };

  const onSubmit = async () => {
    if (!consent || !sourceHost || parsed.length === 0) return;
    setSubmitting(true);
    setReport(null);
    try {
      const r = await fetch(getApiUrl('/email/migrations/bulk'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({
          mailDomainId: domainRowId,
          sourceHost,
          sourcePort: Number(sourcePort) || 993,
          sourceUseSsl: useSsl,
          customerConsent: true,
          rows: parsed,
        }),
      });
      const j = await r.json();
      if (!r.ok || !j?.success) {
        setReport([{ row: 0, destEmail: '(request)', ok: false, error: j?.error || `HTTP ${r.status}` }]);
      } else {
        setReport(j.data.results as BulkMigResult[]);
      }
    } catch (e: any) {
      setReport([{ row: 0, destEmail: '(network)', ok: false, error: e?.message || 'request failed' }]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <div className="text-xs text-slate-600">
        One CSV row per mailbox: <code>source_user,source_password,dest_email,dest_password</code>.
        Leave <code>dest_email</code> blank to default to <code>source_user@{domain}</code>.
        Leave <code>dest_password</code> blank to reuse <code>source_password</code>. Lines starting with <code>#</code> are ignored.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input value={sourceHost} onChange={e => setSourceHost(e.target.value)} placeholder="Source IMAP host"
          className="text-xs px-2 py-1.5 rounded border border-slate-300" />
        <input value={sourcePort} onChange={e => setSourcePort(e.target.value)} placeholder="993"
          className="text-xs px-2 py-1.5 rounded border border-slate-300" />
        <label className="text-xs inline-flex items-center gap-1">
          <input type="checkbox" checked={useSsl} onChange={e => setUseSsl(e.target.checked)} />
          Use SSL
        </label>
      </div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={6}
        placeholder={`alice@old.example,oldpass,alice@${domain},newpass\nbob@old.example,oldpass2`}
        className="w-full font-mono text-xs rounded border border-slate-300 p-2"
      />
      <label className="flex items-start gap-2 text-xs text-slate-700">
        <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5" />
        <span>
          I authorize 12and22 to connect to the source IMAP accounts above with these credentials
          and copy their mail to the destination mailboxes.
        </span>
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs text-slate-700 cursor-pointer">
          <input type="file" accept=".csv,text/csv,text/plain" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
          <span className="px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Choose CSV…</span>
        </label>
        <button type="button" onClick={onParse}
          className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">
          Parse
        </button>
        <button type="button" onClick={onSubmit}
          disabled={submitting || !consent || !sourceHost || parsed.length === 0}
          className="text-xs px-3 py-1.5 rounded bg-primary-navy text-white font-medium hover:bg-primary-700 disabled:opacity-50">
          {submitting ? 'Queueing…' : `Queue ${parsed.length || ''} migration${parsed.length === 1 ? '' : 's'}`}
        </button>
      </div>
      {parseErrors.length > 0 && (
        <ul role="alert" className="text-xs text-primary-navy list-disc pl-5">
          {parseErrors.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
      {parsed.length > 0 && !report && (
        <div className="text-xs text-slate-600">{parsed.length} valid row{parsed.length === 1 ? '' : 's'} ready.</div>
      )}
      {report && (
        <div className="text-xs space-y-1">
          <div className="font-medium">
            {report.filter(r => r.ok).length} queued, {report.filter(r => !r.ok).length} failed
          </div>
          <ul className="max-h-48 overflow-y-auto border border-slate-200 rounded p-2 bg-slate-50">
            {report.map((r, i) => (
              <li key={i} className={r.ok ? 'text-primary-navy' : 'text-primary-navy font-medium'}>
                {r.ok ? '✓' : '✗'} {r.destEmail}{r.error ? ` — ${r.error}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ProviderHints: React.FC<{ provider?: SourceProvider }> = ({ provider }) => {
  if (!provider || provider === 'unknown' || provider === 'other') return null;
  const hint =
    provider === 'gmail' ? (
      <>
        <strong>Gmail / Workspace:</strong> Enable IMAP in the user's Gmail settings (gear → See all settings → Forwarding and POP/IMAP → IMAP access: Enable). For accounts with 2FA, generate an <em>app password</em> at <code>myaccount.google.com/apppasswords</code> and use that here instead of the regular password. Source host: <code>imap.gmail.com</code>, port <code>993</code>, SSL.
      </>
    ) : provider === 'm365' ? (
      <>
        <strong>Microsoft 365:</strong> IMAP must be enabled in the M365 admin center (Users → user → Mail → Manage email apps → IMAP). Source host: <code>outlook.office365.com</code>, port <code>993</code>, SSL. Modern Auth (OAuth) is required by default; for now use an account with basic auth enabled, or contact us if you need OAuth.
      </>
    ) : provider === 'zoho' ? (
      <>
        <strong>Zoho Mail:</strong> Enable IMAP in Zoho Mail settings (Settings → Mail Accounts → IMAP). Source host: <code>imap.zoho.com</code>, port <code>993</code>, SSL.
      </>
    ) : null;
  return (
    <div className="mb-4 rounded-xl bg-secondary-50 border border-secondary-200 p-4 text-sm text-primary-navy">
      {hint}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 8 — Cutover

const StepCutover: React.FC<{ state: OnboardingState; onNext: () => void; onBack: () => void }> = ({ state, onNext, onBack }) => {
  const id = useDomainRowId();
  const mail = useMailSettings(id);
  return (
  <Card
    title="Cutover — flip the switch"
    subtitle="This is the moment new incoming mail starts arriving in your new mailboxes here instead of at your old provider."
    footer={
      <>
        <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">← Back</button>
        <button onClick={onNext} className="text-sm bg-primary-navy text-white font-medium px-4 py-2 rounded-lg hover:bg-primary-700 transition">
          I'm done, finish →
        </button>
      </>
    }
  >
    <ol className="list-decimal list-inside space-y-3 text-sm text-slate-700">
      <li>
        <strong>Confirm your MX is live.</strong> Your DNS step above already updated the MX to point at our mail servers. If you skipped it, do that first.
      </li>
      <li>
        <strong>Run a final delta sync.</strong> If you migrated mail, messages that arrived during the migration may still be on the old server. Go back to Step 7 and press <em>Delta sync</em> on each finished job to catch the last few messages.
      </li>
      <li>
        <strong>Send yourself a test.</strong> From a personal account (Gmail / Yahoo), send a message to <code>{`anyone@${state.domain}`}</code>. It should arrive in <Link to={`/dashboard/email/mailboxes/v2/${id}`} className="text-primary-navy underline">your mailbox</Link> within a minute.
      </li>
      <li>
        <strong>Tell your team.</strong> Send a heads-up: "Mail is now on 12and22. Webmail is at <code>{mail.webmailUrl}</code>. IMAP host: <code>{mail.imapHost}</code> port {mail.imapPort}, SMTP host <code>{mail.smtpHost}</code> port {mail.smtpPort}."
      </li>
    </ol>
  </Card>
  );
};

// ---------------------------------------------------------------------------
// Step 9 — Done

const StepDone: React.FC<{ state: OnboardingState; onFinish: () => void }> = ({ state, onFinish }) => {
  const id = useDomainRowId();
  const mail = useMailSettings(id);
  return (
  <Card
    title={`You're live on ${state.domain}`}
    subtitle="That's the whole setup. Bookmark these details for your team."
    footer={
      <>
        <span />
        <button onClick={onFinish} className="text-sm bg-secondary-500 text-primary-navy font-semibold px-4 py-2 rounded-lg hover:bg-secondary-600 transition">
          Go to email dashboard →
        </button>
      </>
    }
  >
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
      <Setting label="Webmail" value={mail.webmailUrl} link />
      <Setting label="IMAP" value={`${mail.imapHost}:${mail.imapPort} (TLS)`} />
      <Setting label="SMTP" value={`${mail.smtpHost}:${mail.smtpPort} (STARTTLS)`} />
      <Setting label="POP3" value={`${mail.pop3Host}:${mail.pop3Port} (TLS)`} />
      <Setting label="Username" value="full email address" />
      <Setting label="Password" value="the password you set per mailbox" />
    </div>
    <div className="mt-5 text-sm text-slate-700 space-y-2">
      <p>From here you can:</p>
      <ul className="list-disc list-inside space-y-1">
        <li><Link to={`/dashboard/email/mailboxes/v2/${id}`} className="text-primary-navy underline">Manage mailboxes</Link> — add, suspend, change passwords, aliases, and vacation auto-replies (under Edit on each mailbox).</li>
        <li><Link to="/customer/email" className="text-primary-navy underline">Add more seats</Link> — billed pro-rated.</li>
        <li>Visit <a href={mail.webmailUrl} target="_blank" rel="noreferrer" className="text-primary-navy underline">webmail</a> to send your first message.</li>
      </ul>
    </div>
  </Card>
  );
};

const Setting: React.FC<{ label: string; value: string; link?: boolean }> = ({ label, value, link }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-3">
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    {link ? (
      <a href={value} target="_blank" rel="noreferrer" className="font-mono text-sm text-primary-navy hover:underline break-all">{value}</a>
    ) : (
      <div className="font-mono text-sm text-primary-navy break-all">{value}</div>
    )}
  </div>
);

// ===========================================================================
// Inline panels — these replace the cross-page iframes the wizard used to
// embed. Each is a stripped-down view of one functional area (DNS, mailboxes,
// migration) that shares state + auth with the wizard and integrates cleanly
// into the Card layout. The full standalone routes (/dashboard/email/setup,
// /mailboxes, /migrate) still exist for direct access from the dashboard.
// ===========================================================================

// ---------- DNS panel ----------

interface DnsRecord { host: string; type: string; value: string; visible: boolean; label?: string; help?: string; kind?: string; required?: boolean }
interface V2SetupResponse extends MailConnectionFields {
  domain: {
    id: string;
    domain: string;
    status: string;
    status_detail?: string | null;
    last_error?: string | null;
    last_error_public?: string | null;
    ownership_verified_at?: string | null;
  };
  records: DnsRecord[];
  events?: Array<{ event_type: string; message: string; created_at: string }>;
  ui_stage: string;
  status_detail?: string | null;
  last_error_public?: string | null;
}

const InlineDnsPanel: React.FC<{ domainRowId: string; onStatus?: (st: DnsStatus) => void }> = ({ domainRowId, onStatus }) => {
  const [data, setData] = useState<V2SetupResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [rechecking, setRechecking] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const j = await api<{ success: boolean; data?: V2SetupResponse; error?: string }>(`/email/setup/v2/${domainRowId}`);
      if (!j.success || !j.data) { setErr(j.error || 'Failed to load DNS records'); return; }
      setData(j.data);
      setErr(null);
      const d = j.data;
      const records = Array.isArray(d.records) ? d.records : [];
      const ownershipVerified =
        !!d.domain?.ownership_verified_at ||
        records.some(r => r.kind === 'ownership' && r.visible) ||
        (Array.isArray(d.events) && d.events.some(e => e.event_type === 'ownership_verified')) ||
        DNS_DONE_STATUSES.has(d.domain?.status);
      onStatus?.({ status: d.domain?.status, uiStage: d.ui_stage, ownershipVerified });
    } catch (e: any) { setErr(e?.message || 'Network error'); }
    finally { setLoading(false); }
  }, [domainRowId, onStatus]);

  useEffect(() => { load(); }, [load]);
  // Re-load every 30s while visible — the backend poller updates
  // published_at timestamps every 60s so we don't need to be faster.
  useEffect(() => {
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      load();
    }, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const recheck = async () => {
    setRechecking(true);
    setErr(null);
    try {
      await api(`/email/setup/v2/${domainRowId}/recheck`, { method: 'POST' });
      await load();
    } catch (e: any) {
      setErr(e?.message || 'Re-check failed');
    } finally { setRechecking(false); }
  };

  if (loading && !data) return <div className="text-sm text-slate-500 p-4" aria-live="polite">Loading DNS records…</div>;
  if (err && !data) return <div role="alert" className="text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded p-3">🚨 {err}</div>;
  if (!data) return null;

  const records = Array.isArray(data.records) ? data.records : [];
  // Optional records (DMARC, mail-app autoconfig) don't count towards "done".
  const requiredRecords = records.filter(r => r.required !== false);
  const visibleCount = requiredRecords.filter(r => r.visible).length;
  const totalCount = requiredRecords.length;
  const status = data.domain?.status;
  const detail = data.status_detail || data.domain?.status_detail;
  const publicError = data.last_error_public || data.domain?.last_error_public || data.domain?.last_error;
  const hasOwnership = records.some(r => r.kind === 'ownership');
  const ownershipRec = records.find(r => r.kind === 'ownership');
  const ownershipPending = !!ownershipRec && !ownershipRec.visible && !data.domain?.ownership_verified_at;

  // Terminal / blocked states get an explicit card with next steps.
  if (data.ui_stage === 'failed' || status === 'failed') {
    return (
      <div role="alert" className="rounded-xl bg-secondary-200 border-2 border-secondary-500 p-4 space-y-2">
        <div className="font-semibold text-primary-navy">🚨 Setup needs attention</div>
        <p className="text-sm text-primary-navy/90">{publicError || 'Provisioning hit a problem we couldn\'t resolve automatically.'}</p>
        {detail && <p className="text-xs text-primary-navy/70">{detail}</p>}
        <div className="flex flex-wrap gap-2 pt-1">
          <Link to="/contact" className="text-sm bg-primary-navy text-white px-3 py-1.5 rounded-lg hover:bg-primary-700">Contact support</Link>
          <button type="button" onClick={recheck} disabled={rechecking} className="text-sm border border-primary-navy text-primary-navy px-3 py-1.5 rounded-lg disabled:opacity-50">
            {rechecking ? 'Checking…' : 'Try again'}
          </button>
        </div>
      </div>
    );
  }
  if (data.ui_stage === 'suspended' || status === 'suspended') {
    return (
      <div role="alert" className="rounded-xl bg-secondary-200 border-2 border-primary-navy p-4 space-y-2">
        <div className="font-semibold text-primary-navy">⚠ This domain is suspended</div>
        <p className="text-sm text-primary-navy/90">{detail || publicError || 'Mail is paused until the suspension is lifted.'}</p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Link to="/dashboard/billing" className="text-sm bg-primary-navy text-white px-3 py-1.5 rounded-lg hover:bg-primary-700">Billing</Link>
          <Link to="/contact" className="text-sm border border-primary-navy text-primary-navy px-3 py-1.5 rounded-lg">Contact support</Link>
        </div>
      </div>
    );
  }
  if (data.ui_stage === 'terminated' || status === 'terminated') {
    return (
      <div role="status" className="rounded-xl bg-gray-100 border-2 border-gray-300 p-4 space-y-2">
        <div className="font-semibold text-primary-navy">· This email service has been cancelled</div>
        <p className="text-sm text-primary-navy/80">{detail || 'Mail for this domain is no longer hosted here.'}</p>
        <Link to="/email" className="text-sm underline text-primary-navy">Browse email plans</Link>
      </div>
    );
  }
  if (data.ui_stage === 'zone_unreachable') {
    return (
      <div role="alert" className="rounded-xl bg-secondary-200 border-2 border-primary-navy p-4 space-y-2">
        <div className="font-semibold text-primary-navy">🚨 Your domain is offline at the registrar</div>
        <p className="text-sm text-primary-navy/90">{publicError || 'The registrar has suspended this domain. Log into your registrar account to fix the cause.'}</p>
        <button type="button" onClick={recheck} disabled={rechecking} className="text-sm border border-primary-navy text-primary-navy px-3 py-1.5 rounded-lg disabled:opacity-50">
          {rechecking ? 'Checking…' : 'Re-check now'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-slate-700" aria-live="polite">
          <span className="font-medium">{visibleCount} of {totalCount}</span> records detected
          {data.ui_stage === 'awaiting_dns' && <span className="text-slate-500"> — checking every 60s</span>}
          {data.ui_stage === 'complete' && <span className="text-primary-navy font-medium"> — all set ✓</span>}
          {data.ui_stage === 'provisioning' && <span className="text-slate-500"> — DNS seen, finishing setup…</span>}
        </div>
        <button
          type="button"
          onClick={recheck}
          disabled={rechecking}
          className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
        >
          {rechecking ? 'Checking…' : 'Re-check now'}
        </button>
      </div>
      {detail && <div className="text-xs text-slate-600">{detail}</div>}
      {err && <div role="alert" className="text-xs text-primary-navy bg-secondary-100 border border-secondary-500 rounded p-2">🚨 {err}</div>}
      {hasOwnership && ownershipPending && (
        <div className="text-xs text-primary-navy bg-secondary-50 border border-secondary-200 rounded p-2">
          Start with the <strong>Domain ownership</strong> record — it proves you control {data.domain.domain}. Once we see it,
          a second batch of records (MX, SPF, DKIM, DMARC) appears here.
        </div>
      )}
      <div className="rounded-lg border border-slate-200 overflow-x-auto bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th scope="col" className="text-left p-2 w-24">Status</th>
              <th scope="col" className="text-left p-2">Record</th>
              <th scope="col" className="text-left p-2">Type</th>
              <th scope="col" className="text-left p-2">Host</th>
              <th scope="col" className="text-left p-2">Value</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => {
              const isOwnership = r.kind === 'ownership';
              return (
                <tr key={i} className="border-t border-slate-100">
                  <td className="p-2 align-top whitespace-nowrap">
                    {r.visible
                      ? <span className="inline-flex items-center gap-1 text-primary-navy font-medium" title="Detected">✓ Detected</span>
                      : <span className="inline-flex items-center gap-1 text-slate-500" title="Not detected yet">⏳ Waiting</span>}
                  </td>
                  <td className="p-2 align-top">
                    <div className="font-medium text-slate-800">{isOwnership ? 'Domain ownership' : (r.label || r.type)}</div>
                    {(r.help || isOwnership) && (
                      <div className="text-slate-500">
                        {r.help || 'Proves you control this domain.'}
                        {isOwnership && ' A second batch of records follows once it\'s seen.'}
                      </div>
                    )}
                  </td>
                  <td className="p-2 align-top font-mono">{r.type}</td>
                  <td className="p-2 align-top font-mono break-all">
                    <CopyText text={r.host} label={`host for ${r.label || r.type}`} />
                  </td>
                  <td className="p-2 align-top font-mono break-all">
                    <CopyText text={r.value} label={`value for ${r.label || r.type}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {publicError && (
        <div role="status" className="text-xs text-primary-navy bg-secondary-50 border border-secondary-200 rounded p-2">
          <strong>Note:</strong> {publicError}
        </div>
      )}
    </div>
  );
};

/** Click-to-copy with an aria-live confirmation. */
const CopyText: React.FC<{ text: string; label: string }> = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable; user can select+copy */ }
  };
  return (
    <button type="button" onClick={copy} className="text-left hover:bg-secondary-100 rounded px-1 -mx-1 break-all" title="Copy" aria-label={`Copy ${label}`}>
      {text}
      <span className="sr-only" aria-live="polite">{copied ? 'Copied to clipboard' : ''}</span>
      {copied && <span className="ml-1 text-[10px] text-primary-navy" aria-hidden>copied</span>}
    </button>
  );
};

// ---------- Mailboxes panel ----------

interface MailboxRow { id: number; email: string; quota_mb?: number; active: boolean }

const InlineMailboxesPanel: React.FC<{ domainRowId: string; domain: string }> = ({ domainRowId, domain }) => {
  const [rows, setRows] = useState<MailboxRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [localPart, setLocalPart] = useState('');
  const [password, setPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const j = await api<{ success: boolean; data?: any[]; error?: string }>(`/email/setup/v2/domains/${domainRowId}/mailboxes`);
      if (!j.success) { setErr(j.error || 'Failed to load mailboxes'); return; }
      setRows((j.data || []).map((m: any) => ({
        id: m.id,
        email: m.email || `${m.local_part}@${domain}`,
        quota_mb: m.quota_mb,
        active: m.active !== false,
      })));
    } catch (e: any) { setErr(e?.message || 'Network error'); }
    finally { setLoading(false); }
  }, [domainRowId, domain]);

  useEffect(() => { load(); }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateErr(null);
    if (!localPart || !password) { setCreateErr('Local part and password are required'); return; }
    setCreating(true);
    try {
      const j = await api<{ success: boolean; error?: string }>(
        `/email/setup/v2/domains/${domainRowId}/mailboxes`,
        { method: 'POST', body: JSON.stringify({ localPart: localPart.toLowerCase(), password }) },
      );
      if (!j.success) { setCreateErr(j.error || 'Create failed'); return; }
      setLocalPart(''); setPassword('');
      await load();
    } catch (e: any) {
      setCreateErr(friendlyError(e, 'Create failed'));
    } finally { setCreating(false); }
  };

  const remove = async (id: number, email: string) => {
    if (!window.confirm(`Delete mailbox ${email}? This removes all stored mail.`)) return;
    setErr(null);
    try {
      await api(`/email/setup/v2/domains/${domainRowId}/mailboxes/${id}`, { method: 'DELETE' });
    } catch (e: any) {
      setErr(friendlyError(e, 'Couldn\'t delete the mailbox'));
    }
    await load();
  };

  return (
    <div className="space-y-4">
      <form onSubmit={create} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
        <div className="text-xs font-medium text-slate-700">Add a single mailbox</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1 flex-1 min-w-[180px]">
            <input
              value={localPart}
              onChange={e => setLocalPart(e.target.value)}
              placeholder="alice"
              aria-label="Mailbox name (the part before @)"
              className="text-sm px-2 py-1.5 rounded border border-slate-300 flex-1 min-w-0"
            />
            <span className="text-sm text-slate-500">@{domain}</span>
          </div>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Mailbox password"
            aria-label="Mailbox password"
            autoComplete="new-password"
            className="text-sm px-2 py-1.5 rounded border border-slate-300 flex-1 min-w-[160px]"
          />
          <button
            type="submit"
            disabled={creating || !localPart || !password}
            className="text-sm px-3 py-1.5 rounded bg-primary-navy text-white font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {creating ? 'Adding…' : 'Add mailbox'}
          </button>
        </div>
        {createErr && <div role="alert" className="text-xs text-primary-navy bg-secondary-100 border border-secondary-500 rounded p-2">🚨 {createErr}</div>}
      </form>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-3 py-2 text-xs font-medium text-slate-700 border-b border-slate-200">
          Existing mailboxes ({rows.length})
        </div>
        {loading ? (
          <div className="text-sm text-slate-500 p-3">Loading…</div>
        ) : err ? (
          <div role="alert" className="text-sm text-primary-navy p-3">🚨 {err}</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500 p-3">No mailboxes yet — add one above.</div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {rows.map(m => (
              <li key={m.id} className="px-3 py-2 flex items-center justify-between text-sm">
                <span className="font-mono break-all">{m.email}</span>
                <button
                  type="button"
                  onClick={() => remove(m.id, m.email)}
                  className="text-xs text-slate-500 hover:text-primary-navy underline"
                  aria-label={`Delete ${m.email}`}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------- Migration panel ----------

interface MigJob {
  id: string;
  destination_email: string;
  source_host: string;
  source_port: number;
  source_username: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  total_messages: number | null;
  transferred_messages: number | null;
  attempt_count: number;
  last_error: string | null;
  last_progress_line: string | null;
  created_at: string;
}

const MIG_DEFAULTS_BY_PROVIDER: Record<string, { host: string; port: number; ssl: boolean }> = {
  gmail: { host: 'imap.gmail.com', port: 993, ssl: true },
  m365:  { host: 'outlook.office365.com', port: 993, ssl: true },
  zoho:  { host: 'imap.zoho.com', port: 993, ssl: true },
};

const InlineMigrationPanel: React.FC<{ domainRowId: string; domain: string; sourceProvider?: SourceProvider }> = ({ domainRowId, domain, sourceProvider }) => {
  const defaults = sourceProvider ? MIG_DEFAULTS_BY_PROVIDER[sourceProvider] : undefined;
  const [destEmail, setDestEmail] = useState('');
  const [destPass, setDestPass] = useState('');
  const [srcHost, setSrcHost] = useState(defaults?.host || '');
  const [srcPort, setSrcPort] = useState(String(defaults?.port || 993));
  const [srcSsl, setSrcSsl] = useState(defaults?.ssl ?? true);
  const [srcUser, setSrcUser] = useState('');
  const [srcPass, setSrcPass] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [jobs, setJobs] = useState<MigJob[]>([]);

  const [listErr, setListErr] = useState<string | null>(null);
  const loadJobs = useCallback(async () => {
    try {
      const j = await api<{ success: boolean; data?: MigJob[]; error?: string }>('/email/migrations');
      if (j.success && Array.isArray(j.data)) {
        // Filter to jobs for this domain — destination_email ends with @<domain>.
        const suffix = `@${domain.toLowerCase()}`;
        setJobs(j.data.filter(x => (x.destination_email || '').toLowerCase().endsWith(suffix)));
        setListErr(null);
      } else {
        setListErr(j.error || 'Couldn\'t load migrations');
      }
    } catch (e: any) { setListErr(e?.message || 'Couldn\'t load migrations'); }
  }, [domain]);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  // Poll active jobs every 15s while the tab is visible.
  useEffect(() => {
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      loadJobs();
    }, 15_000);
    return () => clearInterval(t);
  }, [loadJobs]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitErr(null);
    if (!consent) { setSubmitErr('You need to authorize the migration to proceed.'); return; }
    setSubmitting(true);
    try {
      const j = await api<{ success: boolean; error?: string; data?: { id: string } }>('/email/migrations', {
        method: 'POST',
        body: JSON.stringify({
          mailDomainId: domainRowId,
          destinationEmail: destEmail.trim().toLowerCase(),
          destinationPassword: destPass,
          sourceHost: srcHost.trim().toLowerCase(),
          sourcePort: Number(srcPort) || 993,
          sourceUseSsl: srcSsl,
          sourceUsername: srcUser.trim(),
          sourcePassword: srcPass,
          customerConsent: true,
        }),
      });
      if (!j.success) { setSubmitErr(j.error || 'Submit failed'); return; }
      setDestPass(''); setSrcPass('');
      await loadJobs();
    } catch (e: any) {
      setSubmitErr(friendlyError(e, 'Submit failed'));
    } finally { setSubmitting(false); }
  };

  const [jobErr, setJobErr] = useState<string | null>(null);
  const [jobBusy, setJobBusy] = useState<string | null>(null);

  const cancelJob = async (jobId: string) => {
    if (!window.confirm('Cancel this migration? Already-copied messages stay; in-flight stops.')) return;
    setJobBusy(jobId); setJobErr(null);
    try {
      await api(`/email/migrations/${jobId}/cancel`, { method: 'POST' });
    } catch (e: any) {
      setJobErr(e?.message || 'Could not cancel this migration');
    } finally { setJobBusy(null); }
    await loadJobs();
  };

  // Delta sync: re-queue a finished job. imapsync resumes via UID matching
  // so only mail that arrived since the previous run gets transferred.
  const resyncJob = async (jobId: string) => {
    if (!window.confirm('Re-run this migration to pull in any new mail at the source? Already-copied messages are skipped.')) return;
    setJobBusy(jobId); setJobErr(null);
    try {
      await api(`/email/migrations/${jobId}/resync`, { method: 'POST' });
    } catch (e: any) {
      setJobErr(e?.message || 'Could not resync this migration');
    } finally { setJobBusy(null); }
    await loadJobs();
  };

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-700">Destination (on 12and22)</div>
            <input value={destEmail} onChange={e => setDestEmail(e.target.value)} placeholder={`you@${domain}`} required aria-label="Destination mailbox email"
              className="w-full text-sm px-2 py-1.5 rounded border border-slate-300" />
            <input type="password" value={destPass} onChange={e => setDestPass(e.target.value)} placeholder="Mailbox password" autoComplete="new-password" required aria-label="Destination mailbox password"
              className="w-full text-sm px-2 py-1.5 rounded border border-slate-300" />
          </div>
          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-700">Source (your old provider)</div>
            <input value={srcHost} onChange={e => setSrcHost(e.target.value)} placeholder="imap.gmail.com" required aria-label="Source IMAP host"
              className="w-full text-sm px-2 py-1.5 rounded border border-slate-300" />
            <div className="grid grid-cols-2 gap-2">
              <input value={srcPort} onChange={e => setSrcPort(e.target.value)} placeholder="993" required aria-label="Source IMAP port" inputMode="numeric"
                className="text-sm px-2 py-1.5 rounded border border-slate-300" />
              <label className="text-xs inline-flex items-center gap-1">
                <input type="checkbox" checked={srcSsl} onChange={e => setSrcSsl(e.target.checked)} />
                Use SSL
              </label>
            </div>
            <input value={srcUser} onChange={e => setSrcUser(e.target.value)} placeholder="source username (full old email)" required aria-label="Source username"
              className="w-full text-sm px-2 py-1.5 rounded border border-slate-300" />
            <input type="password" value={srcPass} onChange={e => setSrcPass(e.target.value)} placeholder="source password (or app password)" autoComplete="new-password" required aria-label="Source password"
              className="w-full text-sm px-2 py-1.5 rounded border border-slate-300" />
          </div>
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5" />
          <span>I authorize 12and22 to connect to the source IMAP account above and copy its mail to my destination mailbox.</span>
        </label>
        {submitErr && <div role="alert" className="text-xs text-primary-navy bg-secondary-100 border border-secondary-500 rounded p-2">🚨 {submitErr}</div>}
        <div className="flex justify-end">
          <button type="submit" disabled={submitting || !consent}
            className="text-sm px-3 py-1.5 rounded bg-primary-navy text-white font-medium hover:bg-primary-700 disabled:opacity-50">
            {submitting ? 'Submitting…' : 'Start migration'}
          </button>
        </div>
      </form>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-3 py-2 text-xs font-medium text-slate-700 border-b border-slate-200">
          Migrations for this domain ({jobs.length})
        </div>
        {(listErr || jobErr) && (
          <div role="alert" className="m-3 text-xs text-primary-navy bg-secondary-100 border border-secondary-500 rounded p-2">🚨 {jobErr || listErr}</div>
        )}
        {jobs.length === 0 ? (
          <div className="text-sm text-slate-500 p-3">No migrations yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {jobs.map(j => (
              <li key={j.id} className="px-3 py-2 text-xs">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="font-mono break-all">
                    {j.source_username} → {j.destination_email}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-primary-navy" title={`Status: ${j.status}`}>
                      {j.status === 'succeeded' ? '✓ succeeded' :
                       j.status === 'failed' ? '🚨 failed' :
                       j.status === 'cancelled' ? '· cancelled' :
                       j.status === 'running' ? '↻ running' : '⏳ queued'}
                    </span>
                    {(j.status === 'queued' || j.status === 'running') && (
                      <button type="button" onClick={() => cancelJob(j.id)} disabled={jobBusy === j.id}
                        className="underline text-primary-navy disabled:opacity-50" aria-label={`Cancel migration to ${j.destination_email}`}>
                        Cancel
                      </button>
                    )}
                    {(j.status === 'succeeded' || j.status === 'failed' || j.status === 'cancelled') && (
                      <button type="button" onClick={() => resyncJob(j.id)} disabled={jobBusy === j.id}
                        className="underline text-primary-navy disabled:opacity-50" title="Re-run to pull in new mail that arrived at the source since last run"
                        aria-label={`Delta sync migration to ${j.destination_email}`}>
                        Delta sync
                      </button>
                    )}
                  </div>
                </div>
                {j.transferred_messages != null && j.total_messages != null && (
                  <div className="text-slate-500 mt-1">
                    {j.transferred_messages} / {j.total_messages} messages
                  </div>
                )}
                {j.last_error && (
                  <div className="text-primary-navy mt-1 break-all"><strong>Error:</strong> {j.last_error}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default BusinessEmailOnboarding;
